import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '@/i18n';
import { platformAPI } from '@/utils/api';
import { formatPriceRangeDisplay } from '@/utils/formatPriceRange';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import SheinRefreshButton from '@/shein-platform/components/SheinRefreshButton';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinSyncJobsOptional } from '@/shein-platform/context/SheinSyncJobContext';
import { useTemuSyncJobsOptional } from '@/temu-platform/context/TemuSyncJobContext';
import { useTiktokSyncJobsOptional } from '@/tiktok-platform/context/TiktokSyncJobContext';
import {
  buildSheinProductTree,
  collectSheinTreeProductIds,
  collectSheinTreeSpuKeys,
  collectSpuProductIds,
  pickSkcRepresentative,
  spuSelectionKey,
  sumSkcStock,
} from '@/shein-platform/utils/sheinProductTree';
import {
  buildTemuProductTree,
  collectTemuTreeProductIds,
  collectTemuTreeSpuKeys,
} from '@/temu-platform/utils/temuProductTree';
import {
  buildTiktokProductTree,
  collectTiktokTreeProductIds,
  collectTiktokTreeSpuKeys,
} from '@/tiktok-platform/utils/tiktokProductTree';
import { mapTiktokLocalPublishRecordTimesByProductId } from '@/tiktok-platform/utils/tiktokLocalPublishRecordStore';
import { buildSheinProductChildPath, openSheinPathInNewTab } from '@/shein-platform/constants/sheinRoutes';
import { buildTemuProductChildPath, openTemuPathInNewTab } from '@/temu-platform/constants/temuRoutes';
import { buildTiktokProductChildPath, openTiktokPathInNewTab } from '@/tiktok-platform/constants/tiktokRoutes';
import { checkSheinProductEditable } from '@/shein-platform/utils/checkSheinProductEditable';
import { checkTemuProductEditable } from '@/temu-platform/utils/checkTemuProductEditable';
import { checkTiktokProductEditable } from '@/tiktok-platform/utils/checkTiktokProductEditable';
import {
  buildSiteNameByAbbrMap as buildSheinSiteNameByAbbrMap,
  resolveSubSiteDisplayName as resolveSheinSubSiteDisplayName,
} from '@/shein-platform/utils/sheinSiteList';
import {
  buildSiteNameByAbbrMap as buildTemuSiteNameByAbbrMap,
  resolveSubSiteDisplayName as resolveTemuSubSiteDisplayName,
} from '@/temu-platform/utils/temuSiteList';
import {
  buildSiteNameByAbbrMap as buildTiktokSiteNameByAbbrMap,
  resolveSubSiteDisplayName as resolveTiktokSubSiteDisplayName,
} from '@/tiktok-platform/utils/tiktokSiteList';
import { isAllShopsScope, isConcreteShopId } from '@/utils/platformShopScope';
import SkuDetailModal, {
  skuMerchantCode,
  skuSizeLabel,
} from '@/shein-platform/modules/products/components/SkuDetailModal';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { logTemuSyncPlatformDump } from '@/temu-platform/utils/temuPlatformJsonConsole';
import SignedImage from '@/components/ipe/SignedImage';
import '@/styles/shein-platform/shein-platform.css';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import WfTasksPagination from './components/WfTasksPagination';
import SitesDetailModal from './components/SitesDetailModal';
import TemuCopyProductSitesModal from '@/temu-platform/modules/products/components/TemuCopyProductSitesModal';
import { nameOfSiteId } from '@/temu-platform/utils/temuDeploymentRegions';
import { buildSiteEntries } from './utils/platformProductSites';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import '@/styles/common/empty-state.css';
import '@/styles/ipe/workbench/IpeWorkflowTasksPage.css';
import '@/styles/ipe/workbench/PlatformProductManagePage.css';
import '@/styles/ipe/workbench/PlatformIntegrationPages.css';

/** 同一同步任务的完成回调只 toast 一次；列表刷新以当前仍挂载的页面为准 */
const handledProductSyncOutcomes = new Set();

/** 表格内只预览 1 条，其余通过弹窗查看，避免把行高撑开 */
const SKU_PREVIEW_LIMIT = 1;

/** 「站点」列最多平铺几个站点，其余收进「查看更多」弹窗（与 SKU 列同一套交互） */
const SITE_PREVIEW_LIMIT = 3;

const DEFAULT_PAGE_SIZE = SHEIN_OPS_DEFAULT_PAGE_SIZE;

/** 操作列「更多」：单条上架 / 下架（TikTok 文案为激活 / 停用） */
function SheinProductMoreMenu({
  disabled = false,
  onShelfOn,
  onShelfOff,
  shelfOnLabel = '上架',
  shelfOffLabel = '下架',
}) {
  const { t } = useTranslation('workbench');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`shein-product-more${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="wf-tasks-actions__link shein-product-more__trigger"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('productTable.more')}
      </button>
      {open ? (
        <div className="shein-product-more__menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="shein-product-more__item"
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onShelfOn?.();
            }}
          >
            {shelfOnLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            className="shein-product-more__item"
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onShelfOff?.();
            }}
          >
            {shelfOffLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}

const PRICE_REVIEW_OPTIONS = [
  { value: 'all', labelKey: 'productFilter.reviewAll' },
  { value: 'pending', labelKey: 'productTable.pendingReview' },
  { value: 'approved', labelKey: 'productTable.reviewApproved' },
  { value: 'rejected', labelKey: 'productTable.reviewRejected' },
  { value: 'unknown', labelKey: 'productTable.reviewUnknown' },
];

const PUBLISH_STATUS_OPTIONS = [
  { value: 'all', labelKey: 'productFilter.statusAll' },
  { value: 'published', labelKey: 'productTable.statusPublished' },
  { value: 'draft', labelKey: 'productTable.draft' },
  { value: 'failed', labelKey: 'productTable.failed' },
  { value: 'offline', labelKey: 'productTable.offline' },
];

const SHEIN_LISTING_DIMENSIONS = [
  { id: 'all', labelKey: 'productTable.all', statKey: 'total' },
  { id: 'listed', labelKey: 'productTable.listed', statKey: 'listed' },
  { id: 'pending', labelKey: 'productTable.pending', statKey: 'pending' },
  { id: 'sold_out', labelKey: 'productTable.soldOut', statKey: 'soldOut' },
  { id: 'offline', labelKey: 'productTable.offline', statKey: 'offline' },
  { id: 'drafts', labelKey: 'productTable.drafts', statKey: 'drafts' },
  { id: 'price_review', labelKey: 'productTable.priceReviewGoods', statKey: 'priceReview', accent: true },
];

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(i18n.language || 'zh-CN', { hour12: false });
  } catch {
    return '—';
  }
}

/** SKC 下多 SKU 价格：相同显示一个，不同显示 min~max */
function formatSkcPriceRange(skus, fallbackCurrency = 'USD') {
  const list = Array.isArray(skus) ? skus : [];
  if (!list.length) return '—';
  return formatPriceRangeDisplay(
    list.map((sku) => ({
      value: sku?.salePrice ?? sku?.supplyPrice,
      currency: sku?.currency,
    })),
    fallbackCurrency,
  );
}

function priceReviewClass(status) {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'failed';
  if (status === 'pending') return 'processing';
  return 'waiting';
}

function isOfflineLike(row) {
  const publish = String(row?.publishStatus || '');
  const audit = String(row?.auditStatus || '');
  const auditLower = audit.toLowerCase();
  return publish === 'offline'
    || audit.trim() === '0'
    || auditLower.includes('off')
    || auditLower.includes('delist')
    || audit.includes('下架');
}

/** 是否为本系统发布留存的创建/发布时间（平台拉取拿不到可靠创建/发布时间） */
function hasSystemPublishTimes(row) {
  return row?.listingJobLineId != null || row?.sourcingCaptureItemId != null;
}

function shelfStatusCode(row) {
  if (row?.priceReviewStatus === 'pending') return 'pendingReview';
  const stock = row?.stockQty;
  const stockSoldOut = stock != null && Number(stock) <= 0;
  const hasListedAt = Boolean(row?.listedAt);
  const publish = String(row?.publishStatus || '').toLowerCase();
  // TikTok 等：审核通过但未激活 → publishStatus=pending → 待上架（产品信息维度）
  if (publish === 'pending') return 'pending';
  if (isOfflineLike(row)) {
    // 平台 shelfStatus=0：待上架无上架时间；已售罄库存为 0；其余为已下架
    if (!hasListedAt) return 'pending';
    if (stockSoldOut) return 'soldOut';
    return 'offline';
  }
  if (stockSoldOut && publish === 'published' && hasListedAt) return 'soldOut';
  const audit = String(row?.auditStatus || '').toLowerCase();
  if (audit.includes('wait') || audit.includes('pend') || audit.includes('待') || audit === 'pre_approved') {
    return 'pending';
  }
  if (publish === 'published') return 'listed';
  if (publish === 'draft') return 'draft';
  if (publish === 'failed') return 'failed';
  return '';
}

function shelfStatusClass(row) {
  const code = shelfStatusCode(row);
  if (code === 'listed') return 'success';
  if (code === 'pendingReview' || code === 'pending') return 'processing';
  if (code === 'soldOut') return 'waiting';
  if (code === 'offline' || code === 'failed') return 'failed';
  return 'waiting';
}

/**
 * 创建日期：本系统发布留存的行只有系统时间；TEMU 货品列表接口直接回带平台
 * 创建时间（毫秒时间戳，后端已转 ISO），需要允许直接展示，否则整列都是「—」。
 * TikTok：本站发品写入发布记录的 createdAt，经 fromLocalPublishRecord 回填后展示。
 */
function createTimeOf(row, allowPlatformTime = false) {
  if (!row?.createdAt) return null;
  if (allowPlatformTime || hasSystemPublishTimes(row) || row?.fromLocalPublishRecord) {
    return row.createdAt;
  }
  return null;
}

function publishTimeOf(row) {
  if (!row?.publishedAt) return null;
  if (hasSystemPublishTimes(row) || row?.fromLocalPublishRecord) {
    return row.publishedAt;
  }
  return null;
}

function listedTimeOf(row) {
  // 上架时间来自 spu 详情 firstShelfTime；待上架通常为空
  return row?.listedAt || null;
}

function formatStock(val) {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return String(n);
}

function pickLatestProductSyncedAt(productItems) {
  let latest = null;
  for (const row of productItems || []) {
    const syncedAt = row?.lastSyncedAt;
    if (!syncedAt) continue;
    if (!latest || new Date(syncedAt).getTime() > new Date(latest).getTime()) {
      latest = syncedAt;
    }
  }
  return latest;
}

function buildSheinKeywordFromFilters(filters) {
  const skc = String(filters.skc || '').trim();
  const sku = String(filters.sku || '').trim();
  const title = String(filters.title || '').trim();
  const supplierCode = String(filters.supplierCode || '').trim();
  const brand = String(filters.brand || '').trim();
  // 后端 keyword 匹配 title / SPU / SKC / SKU；优先精确字段
  return skc || sku || title || supplierCode || brand || '';
}

function escapeCsvCell(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/**
 * TEMU 不导出价格列：平台「货品申报价查询」属自研专属接口（ISV 必然 7000022），
 * 「货品列表」响应里也没有价格字段 → 只会导出两列空值。
 */
function downloadProductsCsv(rows, filename, { includePrice = true } = {}) {
  const headers = [
    'id', 'title', 'platformSpuId', 'platformSkcId', 'platformSkuId',
    'publishStatus', 'priceReviewStatus',
    ...(includePrice ? ['salePrice', 'supplyPrice'] : []),
    'stockQty', 'salesLast7Days', 'lastSyncedAt',
  ];
  const lines = [headers.join(',')];
  for (const row of rows) {
    lines.push(headers.map((key) => escapeCsvCell(row?.[key])).join(','));
  }
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** TEMU 指定同步用 productSkcIds；SHEIN 用 spuNames */
function describeProductSyncStart(syncPayload, isTemu) {
  const text = (key, vars) => i18n.t(`productAction.${key}`, { ns: 'workbench', ...vars });
  if (isTemu) {
    const skcIds = Array.isArray(syncPayload?.productSkcIds) ? syncPayload.productSkcIds : [];
    if (skcIds.length > 0) return text('syncStartSkc', { count: skcIds.length });
    return text('syncStartAll');
  }
  const spuNames = Array.isArray(syncPayload?.spuNames) ? syncPayload.spuNames : [];
  if (spuNames.length > 0) return text('syncStartSpu', { count: spuNames.length });
  return text('syncStartAll');
}

export default function PlatformProductManagePage({
  showToast,
  embedded = false,
  workspace,
  forcedShopId = '',
  /** 全部店铺聚合时必填（如 半托管） */
  shopTypeFilter = '',
  sheinExternalToolbar = false,
  enableSheinOpsToolbar = false,
  /** 运营台模式：不渲染内置工具栏/维度 Tab，由外层页面托管 */
  opsExternalChrome = false,
  initialListingDimension = 'listed',
  lockListingDimension = false,
  hideDimensionTabs = false,
  controlledListingDimension,
  onListingDimensionChange,
  controlledKeyword,
  /** 运营台结构化查询：{ idType, keywords, title, brand, supplierCode } */
  controlledQuery,
  onDimensionStatsChange,
  onSelectionChange,
  filterSlot = null,
  statusTabsSlot = null,
  batchBarSlot = null,
  onSheinSyncStateChange,
  onTemuSyncStateChange,
  onTiktokSyncStateChange,
  /** TEMU 半托管：US/EU/GLOBAL 网关区筛选 */
  controlledGatewayRegion,
}) {
  const { t } = useTranslation('workbench');
  const isSheinWorkspace = workspace === 'shein';
  const isTemuWorkspace = workspace === 'temu';
  const isTiktokWorkspace = workspace === 'tiktok';
  // TEMU 价格不展示：平台「货品申报价查询」属自研专属接口（ISV 应用必然 7000022），
  // 「货品列表」响应里也没有任何价格字段，本地 temu_goods_sku.supplier_price 亦无回填值，
  // 因此价格列在 TEMU 侧只能是「—」。与其展示一片无意义的「—」，不如整列隐藏。
  // （SHEIN / TikTok 价格可正常取到，列维持不变。）
  const showPriceColumn = !isTemuWorkspace;
  /** SHEIN / TEMU / TikTok 产品信息共用 SPU→SKC 树形表；TikTok 列集对齐 SHEIN（含 SKU） */
  const useOpsProductTable = isSheinWorkspace || isTemuWorkspace || isTiktokWorkspace;
  const platformBrand = isSheinWorkspace
    ? 'SHEIN'
    : (isTemuWorkspace ? 'TEMU' : (isTiktokWorkspace ? 'TikTok' : t('productAction.platform')));
  const exportFilePrefix = isSheinWorkspace
    ? 'shein'
    : (isTemuWorkspace ? 'temu' : (isTiktokWorkspace ? 'tiktok' : 'platform'));
  const api = isSheinWorkspace
    ? sheinPlatformAPI
    : (isTemuWorkspace ? temuPlatformAPI : (isTiktokWorkspace ? tiktokPlatformAPI : platformAPI));
  const sheinSyncJobs = useSheinSyncJobsOptional();
  const temuSyncJobs = useTemuSyncJobsOptional();
  const tiktokSyncJobs = useTiktokSyncJobsOptional();
  const syncJobs = isTemuWorkspace
    ? temuSyncJobs
    : (isTiktokWorkspace ? tiktokSyncJobs : sheinSyncJobs);
  const showPlatformConfirm = isTemuWorkspace
    ? showTemuConfirm
    : (isTiktokWorkspace ? showTiktokConfirm : showSheinConfirm);
  const resolveSiteDisplayName = isTemuWorkspace
    ? resolveTemuSubSiteDisplayName
    : (isTiktokWorkspace ? resolveTiktokSubSiteDisplayName : resolveSheinSubSiteDisplayName);
  const ProductThumb = isTemuWorkspace
    ? TemuProductThumb
    : (isTiktokWorkspace ? TiktokProductThumb : SheinProductThumb);
  const [loading, setLoading] = useState(false);
  const [editingSpuId, setEditingSpuId] = useState('');
  const [shops, setShops] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  /** 后端给的总页数（TEMU 按 SPU 分页，比 ceil(total/pageSize) 准）；缺省时前端按 total 推算 */
  const [serverTotalPages, setServerTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [shopFilter, setShopFilter] = useState('');
  const [priceReviewFilter, setPriceReviewFilter] = useState('all');
  const [publishFilter, setPublishFilter] = useState('all');
  const [listingDimensionInternal, setListingDimensionInternal] = useState(initialListingDimension || 'listed');
  const listingDimension = controlledListingDimension != null
    ? controlledListingDimension
    : listingDimensionInternal;
  const setListingDimension = (next) => {
    if (controlledListingDimension != null) {
      onListingDimensionChange?.(next);
    } else {
      setListingDimensionInternal(next);
    }
  };
  const [dimensionStats, setDimensionStats] = useState(null);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keywordInternal, setKeywordInternal] = useState('');
  const keyword = controlledKeyword != null ? controlledKeyword : keywordInternal;
  const setKeyword = (next) => {
    if (controlledKeyword == null) setKeywordInternal(next);
  };
  const [queryNonce, setQueryNonce] = useState(0);
  const queryKey = controlledQuery
    ? [
      controlledQuery.idType || '',
      controlledQuery.keywords || '',
      controlledQuery.title || '',
      controlledQuery.brand || '',
      controlledQuery.supplierCode || '',
      controlledQuery._nonce || '',
      controlledGatewayRegion || '',
    ].join('|')
    : `${keyword}|${queryNonce}|${controlledGatewayRegion || ''}`;
  const [filterDraft, setFilterDraft] = useState({
    skc: '',
    supplierCode: '',
    title: '',
    brand: '',
    sku: '',
  });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  /** SHEIN：选中的 SPU（工具栏数量与批量删除按此维度） */
  const [selectedSpuIds, setSelectedSpuIds] = useState(() => new Set());
  const [lastProductsSyncedAt, setLastProductsSyncedAt] = useState('');
  const [skuModalSkc, setSkuModalSkc] = useState(null);
  /** 「站点」列「查看更多」：{ title, skcId, specLabel, sites } */
  const [sitesModal, setSitesModal] = useState(null);
  const [copyingSpuId, setCopyingSpuId] = useState('');
  /** TEMU 复制到草稿：站点选择 { spuId, title } */
  const [copySiteModal, setCopySiteModal] = useState(null);
  const [siteNameByAbbr, setSiteNameByAbbr] = useState(() => new Map());
  /** TikTok：当前店铺可售站点展示名（产品无 listedSites 时作站点列兜底） */
  const [tiktokShopSiteNames, setTiktokShopSiteNames] = useState([]);
  const hasLoadedListRef = useRef(false);
  const lastBusyQueryKeyRef = useRef('');

  const opsProductTree = useMemo(() => {
    if (isSheinWorkspace) return buildSheinProductTree(items);
    if (isTemuWorkspace) return buildTemuProductTree(items);
    if (isTiktokWorkspace) {
      const withShopSites = tiktokShopSiteNames.length
        ? items.map((item) => (
          Array.isArray(item?.listedSites) && item.listedSites.length
            ? item
            : { ...item, listedSites: tiktokShopSiteNames, shopSites: tiktokShopSiteNames }
        ))
        : items;
      return buildTiktokProductTree(withShopSites);
    }
    return [];
  }, [items, isSheinWorkspace, isTemuWorkspace, isTiktokWorkspace, tiktokShopSiteNames]);

  const shopOptions = useMemo(
    () => {
      const code = isSheinWorkspace ? 'SHEIN' : (isTemuWorkspace ? 'TEMU' : (isTiktokWorkspace ? 'TIKTOK' : ''));
      return [
        { value: '', label: t('productFilter.allShops') },
        ...shops
          .filter((s) => !code || String(s.platformCode || '').toUpperCase() === code)
          .map((s) => ({ value: String(s.id), label: s.shopName || t('productFilter.shopFallback', { id: s.id }) })),
      ];
    },
    [shops, isSheinWorkspace, isTemuWorkspace, isTiktokWorkspace, t],
  );

  const priceReviewOptions = useMemo(
    () => PRICE_REVIEW_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })),
    [t],
  );
  const publishStatusOptions = useMemo(
    () => PUBLISH_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })),
    [t],
  );

  const loadShops = useCallback(async () => {
    try {
      const list = await platformAPI.listShops();
      setShops(Array.isArray(list) ? list : []);
    } catch (e) {
      showToast?.(e.message || t('productFilter.loadShopsFailed'), 'error');
    }
  }, [showToast, t]);

  const effectiveShopFilter = (isSheinWorkspace || isTemuWorkspace || isTiktokWorkspace)
    ? String(forcedShopId || '')
    : shopFilter;
  // TEMU / TikTok 列表多为单店 API：全部视图不走 allOfType，由外层要求先选具体店
  const allShopsMode = isSheinWorkspace && isAllShopsScope(effectiveShopFilter);
  const concreteShopId = isConcreteShopId(effectiveShopFilter) ? String(effectiveShopFilter) : '';

  useEffect(() => {
    let cancelled = false;
    async function loadSiteNames() {
      if (!useOpsProductTable || !concreteShopId) {
        setSiteNameByAbbr(new Map());
        setTiktokShopSiteNames([]);
        return;
      }
      try {
        const res = isTemuWorkspace
          ? await temuPlatformAPI.getShopSiteList(Number(concreteShopId))
          : (isTiktokWorkspace
            ? await tiktokPlatformAPI.getShopSiteList(Number(concreteShopId))
            : await sheinPlatformAPI.getShopSiteList(Number(concreteShopId)));
        if (cancelled) return;
        const buildMap = isTemuWorkspace
          ? buildTemuSiteNameByAbbrMap
          : (isTiktokWorkspace ? buildTiktokSiteNameByAbbrMap : buildSheinSiteNameByAbbrMap);
        const map = buildMap(res?.siteList ?? res);
        setSiteNameByAbbr(map);
        if (isTiktokWorkspace) {
          const names = Array.from(new Set(
            Array.from(map.values()).map((n) => String(n || '').trim()).filter(Boolean),
          ));
          setTiktokShopSiteNames(names);
        } else {
          setTiktokShopSiteNames([]);
        }
      } catch {
        if (!cancelled) {
          setSiteNameByAbbr(new Map());
          setTiktokShopSiteNames([]);
        }
      }
    }
    loadSiteNames();
    return () => { cancelled = true; };
  }, [useOpsProductTable, isTemuWorkspace, isTiktokWorkspace, concreteShopId]);

  // 筛选条件变化时清空跨页选中；翻页/改每页条数保留
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedSpuIds(new Set());
    setPage(1);
  }, [effectiveShopFilter, priceReviewFilter, publishFilter, listingDimension, queryKey]);

  /**
   * TEMU：SKU 明细按需加载（弹窗打开时才请求）。
   *
   * 列表接口每个 SKC 只回 1 条预览 SKU 行以压缩响应体与解析开销，明细由这个回调补齐。
   */
  const loadTemuSkcSkus = useCallback(async (skc) => {
    const shopId = String(concreteShopId || effectiveShopFilter || '').trim();
    const productId = String(skc?.spuId || '').trim();
    const skcId = String(skc?.skcId || '').trim();
    if (!shopId || !productId || !skcId || skcId === '—') {
      throw new Error(t('productAction.missingShopSkc'));
    }
    const res = await temuPlatformAPI.listProductSkus(shopId, productId, skcId);
    return Array.isArray(res?.items) ? res.items : [];
  }, [concreteShopId, effectiveShopFilter, t]);

  const loadDimensionStats = useCallback(async () => {
    if (!useOpsProductTable || (!concreteShopId && !(allShopsMode && shopTypeFilter))) {
      setDimensionStats(null);
      return;
    }
    try {
      const statsParams = {};
      if (controlledQuery) {
        if (controlledQuery.idType) statsParams.idType = controlledQuery.idType;
        if (controlledQuery.keywords) statsParams.keywords = controlledQuery.keywords;
        if (controlledQuery.title) statsParams.title = controlledQuery.title;
        if (controlledQuery.brand) statsParams.brand = controlledQuery.brand;
        if (controlledQuery.supplierCode) statsParams.supplierCode = controlledQuery.supplierCode;
      } else if (keyword) {
        statsParams.keyword = keyword;
      }
      if (isTemuWorkspace && controlledGatewayRegion && controlledGatewayRegion !== 'ALL') {
        statsParams.gatewayRegion = controlledGatewayRegion;
      }
      let stats;
      if (allShopsMode) {
        stats = await api.getProductListingStats({
          allOfType: true,
          shopType: shopTypeFilter,
          ...statsParams,
        });
      } else {
        stats = await api.getProductListingStats(Number(concreteShopId), statsParams);
      }
      setDimensionStats(stats);
    } catch {
      setDimensionStats(null);
    }
  }, [api, controlledQuery, controlledGatewayRegion, concreteShopId, allShopsMode, shopTypeFilter, useOpsProductTable, keyword, queryKey, isTemuWorkspace]);

  const loadProducts = useCallback(async (opts = {}) => {
    const silent = opts?.silent === true;
    const forceBusy = opts?.busy === true;
    // 查询条件变化 / 显式刷新才遮罩；切维度/翻页静默；silent 由外层自行控制 loading
    const queryChanged = lastBusyQueryKeyRef.current !== queryKey;
    lastBusyQueryKeyRef.current = queryKey;
    const blockUi = !silent && (forceBusy || queryChanged || !hasLoadedListRef.current);
    if (blockUi) setLoading(true);
    try {
      if ((isTemuWorkspace || isTiktokWorkspace) && !concreteShopId) {
        setItems([]);
        setTotal(0);
        setServerTotalPages(0);
        return;
      }
      if (isSheinWorkspace && allShopsMode && !shopTypeFilter) {
        setItems([]);
        setTotal(0);
        setServerTotalPages(0);
        return;
      }
      const res = await api.listProducts({
        page,
        pageSize,
        shopId: allShopsMode ? undefined : (concreteShopId || effectiveShopFilter || undefined),
        allOfType: allShopsMode ? true : undefined,
        shopType: allShopsMode ? shopTypeFilter : undefined,
        priceReviewStatus: useOpsProductTable ? 'all' : priceReviewFilter,
        publishStatus: useOpsProductTable ? 'all' : publishFilter,
        listingDimension: useOpsProductTable ? listingDimension : undefined,
        keyword: controlledQuery ? undefined : (keyword || undefined),
        idType: controlledQuery?.idType || undefined,
        keywords: controlledQuery?.keywords || undefined,
        title: controlledQuery?.title || undefined,
        brand: controlledQuery?.brand || undefined,
        supplierCode: controlledQuery?.supplierCode || undefined,
        gatewayRegion: isTemuWorkspace && controlledGatewayRegion && controlledGatewayRegion !== 'ALL'
          ? controlledGatewayRegion
          : undefined,
      });
      let rows = Array.isArray(res?.items) ? res.items : [];
      // TikTok：本站发品的创建/发布日期在本地发布记录，按 product_id 回填到列表
      if (isTiktokWorkspace && concreteShopId && rows.length) {
        const timesMap = mapTiktokLocalPublishRecordTimesByProductId(concreteShopId);
        if (timesMap.size) {
          rows = rows.map((row) => {
            const pid = String(row?.productId || row?.platformSpuId || row?.id || '').trim();
            const times = pid ? timesMap.get(pid) : null;
            if (!times) return row;
            return {
              ...row,
              createdAt: row.createdAt || times.createdAt || null,
              publishedAt: row.publishedAt || times.publishedAt || null,
              fromLocalPublishRecord: true,
            };
          });
        }
      }
      const firstLoadForShop = !hasLoadedListRef.current;
      setItems(rows);
      setTotal(Number(res?.total) || 0);
      setServerTotalPages(Number(res?.totalPages) > 0 ? Number(res.totalPages) : 0);
      hasLoadedListRef.current = true;
      // TEMU：首次加载产品信息页时，把最近一次「同步商品」的平台原始 JSON 打到控制台
      if (isTemuWorkspace && firstLoadForShop) {
        const dumpShopId = String(concreteShopId || effectiveShopFilter || '').trim();
        if (dumpShopId) {
          logTemuSyncPlatformDump(dumpShopId, 'product', {
            label: '商品',
            reason: '打开产品信息页',
          });
        }
      }
      // 跨页选中：翻页刷新列表时保留已选
      if (useOpsProductTable) {
        const syncedAt = pickLatestProductSyncedAt(rows);
        if (syncedAt) {
          setLastProductsSyncedAt(syncedAt);
        }
      }
    } catch (e) {
      showToast?.(e.message || t('productAction.loadProductsFailed'), 'error');
      setItems([]);
      setTotal(0);
      setServerTotalPages(0);
    } finally {
      if (blockUi) setLoading(false);
    }
  }, [page, pageSize, effectiveShopFilter, concreteShopId, allShopsMode, shopTypeFilter, priceReviewFilter, publishFilter, listingDimension, keyword, queryKey, controlledQuery, controlledGatewayRegion, showToast, api, useOpsProductTable, isTemuWorkspace, isTiktokWorkspace, t]);

  useEffect(() => {
    if (!isSheinWorkspace && !isTemuWorkspace && !isTiktokWorkspace) {
      loadShops();
    }
  }, [loadShops, isSheinWorkspace, isTemuWorkspace, isTiktokWorkspace]);

  useEffect(() => {
    if ((isSheinWorkspace || isTemuWorkspace || isTiktokWorkspace) && forcedShopId !== undefined) {
      setPage(1);
      setLastProductsSyncedAt('');
      hasLoadedListRef.current = false;
    }
  }, [forcedShopId, isSheinWorkspace, isTemuWorkspace, isTiktokWorkspace]);

  useEffect(() => {
    if (!lockListingDimension || controlledListingDimension != null) return;
    setListingDimensionInternal(initialListingDimension || 'listed');
  }, [initialListingDimension, lockListingDimension, controlledListingDimension]);

  useEffect(() => {
    if (controlledKeyword != null || controlledQuery != null) {
      setPage(1);
    }
  }, [controlledKeyword, queryKey, controlledQuery]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // 维度 Tab 数量随查询条件变化；切维度不重复拉 stats（避免卡顿）
  useEffect(() => {
    loadDimensionStats();
  }, [loadDimensionStats]);

  useEffect(() => {
    onDimensionStatsChange?.(dimensionStats);
  }, [dimensionStats, onDimensionStatsChange]);

  const selectionNotifyRef = useRef(onSelectionChange);
  selectionNotifyRef.current = onSelectionChange;
  useEffect(() => {
    if (useOpsProductTable) {
      selectionNotifyRef.current?.({
        ids: Array.from(selectedIds),
        spuIds: Array.from(selectedSpuIds),
        count: selectedSpuIds.size,
      });
      return;
    }
    selectionNotifyRef.current?.(Array.from(selectedIds));
  }, [selectedIds, selectedSpuIds, useOpsProductTable]);

  const totalPages = Math.max(
    1,
    serverTotalPages > 0 ? serverTotalPages : Math.ceil(total / pageSize),
  );

  const onQuery = () => {
    if (isSheinWorkspace && enableSheinOpsToolbar) {
      setKeyword(buildSheinKeywordFromFilters(filterDraft));
    } else {
      setKeyword(keywordDraft.trim());
    }
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const onReset = () => {
    setShopFilter('');
    setPriceReviewFilter('all');
    setPublishFilter('all');
    if (!lockListingDimension) {
      setListingDimension(initialListingDimension || 'listed');
    }
    setKeywordDraft('');
    setKeyword('');
    setFilterDraft({ skc: '', supplierCode: '', title: '', brand: '', sku: '' });
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const updateFilterDraft = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onBatchSyncPrice = async () => {
    const ids = Array.from(selectedIds).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast?.(t('productAction.pickProducts'), 'warning');
      return;
    }
    if (!effectiveShopFilter) {
      showToast?.(t('productAction.pickShop'), 'warning');
      return;
    }
    setBatchBusy(true);
    try {
      await api.batchSyncProductPrice(Number(effectiveShopFilter), ids);
      showToast?.(t('productAction.priceRefreshSubmitted', { count: ids.length }), 'success');
      await loadProducts();
      await loadDimensionStats();
    } catch (e) {
      showToast?.(e.message || t('productAction.batchPriceFailed'), 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const onExportSelected = useCallback(() => {
    const ids = selectedIds;
    const rows = items.filter((row) => ids.has(String(row.id)));
    if (!rows.length) {
      showToast?.(t('productAction.pickExport'), 'warning');
      return;
    }
    downloadProductsCsv(rows, `${exportFilePrefix}-products-selected-${Date.now()}.csv`, {
      includePrice: showPriceColumn,
    });
    setExportMenuOpen(false);
    showToast?.(t('productAction.exported', { count: rows.length }), 'success');
  }, [selectedIds, items, showToast, exportFilePrefix, showPriceColumn, t]);

  const onExportPage = useCallback(() => {
    if (!items.length) {
      showToast?.(t('productAction.pageEmptyExport'), 'warning');
      return;
    }
    downloadProductsCsv(items, `${exportFilePrefix}-products-page-${Date.now()}.csv`, {
      includePrice: showPriceColumn,
    });
    setExportMenuOpen(false);
    showToast?.(t('productAction.exportedPage', { count: items.length }), 'success');
  }, [items, showToast, exportFilePrefix, showPriceColumn, t]);

  const onExportFilteredHint = () => {
    setExportMenuOpen(false);
    showToast?.(t('productAction.exportFilterHint'), 'info');
    onExportPage();
  };

  const onSyncShop = async () => {
    const targetShopId = String(effectiveShopFilter || forcedShopId || '').trim();
    if (!targetShopId) {
      showToast?.(t('productAction.pickSyncShop', { platform: platformBrand }), 'warning');
      return;
    }
    setLoading(true);
    try {
      const res = await api.syncShopProducts(Number(targetShopId));
      const count = res?.importedOrUpdated ?? 0;
      const domain = res?.apiDomain ? t('productAction.domainWrap', { domain: res.apiDomain }) : '';
      showToast?.(
        t('productAction.syncedFrom', { platform: platformBrand, count, domain }),
        count > 0 ? 'success' : 'warning',
      );
      await loadProducts();
      await loadDimensionStats();
    } catch (e) {
      showToast?.(e.message || t('productAction.syncFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const forcedShopIdRef = useRef(forcedShopId);
  forcedShopIdRef.current = forcedShopId;
  const [syncProgress, setSyncProgress] = useState(null);
  const [cancellingSync, setCancellingSync] = useState(false);
  const syncJobIdRef = useRef(null);
  const syncTrackIdRef = useRef(null);
  const pageMountedRef = useRef(true);
  const reloadedProductSyncKeysRef = useRef(new Set());
  const boundProductSyncDoneIdsRef = useRef(new Set());
  const applyProductSyncOutcomeRef = useRef(null);
  const syncJobsRef = useRef(syncJobs);
  syncJobsRef.current = syncJobs;

  useEffect(() => {
    pageMountedRef.current = true;
    return () => {
      pageMountedRef.current = false;
    };
  }, []);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const applyProductSyncOutcome = useCallback(async (result, trackId) => {
    const key = String(trackId || '').trim();
    const alreadyHandled = key && handledProductSyncOutcomes.has(key);
    if (key) handledProductSyncOutcomes.add(key);

    if (!alreadyHandled) {
      if (result?.cancelled) {
        showToast?.(t('productAction.syncStopped'), 'info');
      } else if (!result?.ok) {
        const latest = result?.view || {};
        const imported = Number(latest.imported) || 0;
        const errMsg = latest.error
          || result?.error?.message
          || latest.message
          || t('productAction.syncFailed');
        showToast?.(
          imported > 0 ? t('productAction.importedPartial', { message: errMsg, count: imported }) : errMsg,
          imported > 0 ? 'warning' : 'error',
        );
      } else {
        const latest = result.view || {};
        const count = Number(latest.imported) || 0;
        const serverMsg = String(latest.message || '').trim();
        const softStop = /平台异常|已停止|部分批次失败|HTTP\s*401|失败/.test(serverMsg);
        // 后端误标 success 时的兜底：0 条且文案像失败 → 错误提示
        if (count <= 0 && softStop) {
          showToast?.(serverMsg || t('productAction.syncFailed'), 'error');
        } else {
          showToast?.(
            serverMsg
              || t('productAction.refreshOk', { count }),
            softStop ? 'warning' : (count > 0 ? 'success' : 'warning'),
          );
        }
      }
      setLastProductsSyncedAt(new Date().toISOString());
    }

    if (!pageMountedRef.current) return;
    const reloadKey = key || '__fallback__';
    if (reloadedProductSyncKeysRef.current.has(reloadKey)) return;
    reloadedProductSyncKeysRef.current.add(reloadKey);
    try {
      await loadProducts({ silent: true });
      await loadDimensionStats();
    } catch {
      /* ignore reload errors */
    }
  }, [loadDimensionStats, loadProducts, showToast, t]);
  applyProductSyncOutcomeRef.current = applyProductSyncOutcome;

  const bindProductSyncDone = useCallback((trackId) => {
    const id = String(trackId || '').trim();
    const jobs = syncJobsRef.current;
    if (!id || !jobs?.onTrackDone) return;
    if (boundProductSyncDoneIdsRef.current.has(id)) return;
    boundProductSyncDoneIdsRef.current.add(id);
    jobs.onTrackDone(id, (payload, track) => {
      syncTrackIdRef.current = null;
      syncJobIdRef.current = null;
      void applyProductSyncOutcomeRef.current?.(payload || {}, track?.id || id);
    });
  }, []);

  const inflightProductTrackIds = useMemo(() => {
    const shopId = String(forcedShopId || effectiveShopFilter || '').trim();
    if (!shopId || !Array.isArray(syncJobs?.tracks)) return [];
    return syncJobs.tracks
      .filter((t) => (
        t?.kind === 'product'
        && String(t.shopId) === shopId
        && (t.status === 'queued' || t.status === 'running')
        && t.id
      ))
      .map((t) => t.id);
  }, [effectiveShopFilter, forcedShopId, syncJobs?.tracks]);

  useEffect(() => {
    inflightProductTrackIds.forEach((id) => {
      const track = (syncJobsRef.current?.tracks || []).find((t) => t.id === id);
      if (track) {
        syncTrackIdRef.current = track.id;
        syncJobIdRef.current = track.jobId || null;
      }
      bindProductSyncDone(id);
    });
  }, [bindProductSyncDone, inflightProductTrackIds]);

  const refreshShopProductsAction = useCallback(async (syncPayload = null) => {
    const targetShopId = String(forcedShopIdRef.current || effectiveShopFilter || '').trim();
    if (!targetShopId) {
      showToast?.(t('productAction.pickShopCorner', { platform: platformBrand }), 'warning');
      return;
    }
    syncJobIdRef.current = null;
    syncTrackIdRef.current = null;
    setSyncProgress(null);

    // 启动即返回：进度走全局 SyncJob，完成后再 toast + 静默刷新列表
    if (syncJobs?.startJob && syncJobs?.onTrackDone) {
      const alreadyBusy = Boolean(syncJobs.isBusy?.('product', targetShopId));
      const track = await syncJobs.startJob({
        kind: 'product',
        shopId: targetShopId,
        label: t('productAction.syncLabel'),
        silent: true,
        payload: syncPayload || null,
      });
      if (!track?.id) {
        throw new Error(t('productAction.noSyncTask'));
      }
      syncTrackIdRef.current = track.id;
      syncJobIdRef.current = track.jobId || null;
      bindProductSyncDone(track.id);
      if (!alreadyBusy) {
        showToast?.(describeProductSyncStart(syncPayload, isTemuWorkspace), 'info');
      }
      return;
    }

    // 无 SyncJob Provider：启动成功即返回，后台轮询进度
    let peakPercent = 0;
    const applyProgress = (next) => {
      const raw = Math.max(0, Math.min(100, Number(next.percent) || 0));
      peakPercent = Math.max(peakPercent, raw);
      setSyncProgress({
        visible: true,
        status: next.status || 'running',
        percent: peakPercent,
        page: Number(next.page) || 0,
        imported: Number(next.imported) || 0,
        message: next.message || t('productAction.syncing'),
      });
    };
    applyProgress({
      status: 'queued',
      percent: 0,
      page: 0,
      imported: 0,
      message: t('productAction.startingSync'),
    });

    const finishFallback = async (result) => {
      await applyProductSyncOutcome(result);
      await sleep(result?.cancelled ? 800 : (result?.ok ? 1200 : 2000));
      setSyncProgress(null);
      syncJobIdRef.current = null;
      setCancellingSync(false);
    };

    if (typeof api.startProductSyncJob === 'function' && typeof api.getProductSyncJob === 'function') {
      const started = await api.startProductSyncJob(Number(targetShopId), syncPayload || {});
      const jobId = started?.jobId;
      if (!jobId) {
        throw new Error(t('productAction.noSyncTaskId'));
      }
      syncJobIdRef.current = jobId;
      applyProgress({
        status: started.status || 'queued',
        percent: Number(started.percent) || 0,
        page: Number(started.page) || 0,
        imported: Number(started.imported) || 0,
        message: started.message || t('productAction.queued'),
      });
      showToast?.(describeProductSyncStart(syncPayload, isTemuWorkspace), 'info');
      void (async () => {
        try {
          let latest = started;
          while (
            latest
            && !latest.done
            && latest.status !== 'success'
            && latest.status !== 'failed'
            && latest.status !== 'cancelled'
          ) {
            await sleep(400);
            latest = await api.getProductSyncJob(Number(targetShopId), jobId);
            if (!latest) break;
            applyProgress({
              status: latest.status || 'running',
              percent: Number(latest.percent) || 0,
              page: Number(latest.page) || 0,
              imported: Number(latest.imported) || 0,
              message: latest.message || t('productAction.syncing'),
            });
          }
          if (latest?.status === 'cancelled') {
            applyProgress({
              status: 'cancelled',
              percent: peakPercent,
              page: Number(latest.page) || 0,
              imported: Number(latest.imported) || 0,
              message: latest.message || t('productAction.syncCancelled'),
            });
            await finishFallback({ ok: false, cancelled: true, view: latest });
            return;
          }
          if (!latest || latest.status === 'failed') {
            applyProgress({
              status: 'failed',
              percent: peakPercent,
              page: Number(latest?.page) || 0,
              imported: Number(latest?.imported) || 0,
              message: latest?.error || latest?.message || t('productAction.syncFailed'),
            });
            await finishFallback({ ok: false, view: latest || {} });
            return;
          }
          applyProgress({
            status: 'success',
            percent: 100,
            page: Number(latest.page) || 0,
            imported: Number(latest.imported) || 0,
            message: latest.message || t('productAction.syncDone', { count: latest.imported || 0 }),
          });
          await finishFallback({ ok: true, view: latest });
        } catch (e) {
          applyProgress({
            status: 'failed',
            percent: peakPercent,
            message: e?.message || t('productAction.refreshFailed'),
          });
          await finishFallback({ ok: false, error: e, view: {} });
        }
      })();
      return;
    }

    showToast?.(describeProductSyncStart(syncPayload, isTemuWorkspace), 'info');
    void (async () => {
      try {
        applyProgress({
          status: 'running',
          percent: 35,
          page: 0,
          imported: 0,
          message: t('productAction.pulling'),
        });
        const res = await api.syncShopProducts(Number(targetShopId));
        const count = res?.importedOrUpdated ?? 0;
        applyProgress({
          status: 'success',
          percent: 100,
          page: 0,
          imported: count,
          message: t('productAction.syncDone', { count }),
        });
        await finishFallback({
          ok: true,
          view: { imported: count, message: t('productAction.refreshOkShort', { count }) },
        });
      } catch (e) {
        applyProgress({
          status: 'failed',
          percent: peakPercent,
          message: e?.message || t('productAction.refreshFailed'),
        });
        await finishFallback({ ok: false, error: e, view: {} });
      }
    })();
  }, [
    api,
    applyProductSyncOutcome,
    bindProductSyncDone,
    effectiveShopFilter,
    isTemuWorkspace,
    platformBrand,
    showToast,
    syncJobs,
    t,
  ]);

  const cancelProductSync = useCallback(async () => {
    if (cancellingSync) return;
    if (syncTrackIdRef.current && syncJobs?.cancelTrack) {
      setCancellingSync(true);
      try {
        await syncJobs.cancelTrack(syncTrackIdRef.current);
      } catch (e) {
        showToast?.(e?.message || t('productAction.cancelFailed'), 'error');
        setCancellingSync(false);
      }
      return;
    }
    const jobId = syncJobIdRef.current;
    const targetShopId = String(forcedShopIdRef.current || effectiveShopFilter || '').trim();
    if (!targetShopId || !jobId) return;
    if (typeof api.cancelProductSyncJob !== 'function') {
      showToast?.(t('productAction.cancelUnsupported'), 'warning');
      return;
    }
    setCancellingSync(true);
    try {
      await api.cancelProductSyncJob(Number(targetShopId), jobId);
    } catch (e) {
      showToast?.(e?.message || t('productAction.cancelFailed'), 'error');
      setCancellingSync(false);
    }
  }, [api, cancellingSync, effectiveShopFilter, showToast, syncJobs, t]);

  const {
    refresh: onRefreshShopProducts,
    refreshing: refreshingProducts,
    cooldownSeconds: refreshProductsCooldown,
  } = useGuardedRefresh(refreshShopProductsAction);

  const [shelving, setShelving] = useState(false);
  const shelvingRef = useRef(false);

  const runModifySkcShelf = useCallback(async (spuNames, shelfState) => {
    const names = (Array.isArray(spuNames) ? spuNames : [])
      .map((s) => String(s || '').trim())
      .filter((s) => s && s !== '—');
    const label = shelfState === 1
      ? (isTiktokWorkspace ? t('productTable.activate') : t('productTable.listOn'))
      : (isTiktokWorkspace ? t('productTable.deactivate') : t('productTable.listOff'));
    if (!names.length) {
      showToast?.(t('productAction.pickShelf', { label }), 'warning');
      return;
    }
    const shopId = String(effectiveShopFilter || forcedShopId || '').trim();
    if (!shopId) {
      showToast?.(t('productAction.pickShop'), 'warning');
      return;
    }
    if (typeof api.modifySkcShelf !== 'function') {
      showToast?.(t('productAction.unsupportedShelf', { label }), 'warning');
      return;
    }
    if (shelvingRef.current) return;
    shelvingRef.current = true;
    setShelving(true);
    try {
      const res = await api.modifySkcShelf(Number(shopId), { spuNames: names, shelfState });
      const ok = Number(res?.successCount ?? 0);
      const fail = Number(res?.failureCount ?? 0);
      const skcs = Number(res?.requestedSkcs ?? names.length);
      const failures = Array.isArray(res?.failures) ? res.failures : [];
      const firstFail = failures[0];
      const failHint = firstFail
        ? t('productAction.failHint', {
          identity: firstFail.identity || '',
          msg: firstFail.msg ? `: ${firstFail.msg}` : '',
        })
        : '';
      const unit = isTiktokWorkspace ? t('productAction.unitProduct') : t('productAction.unitSkc');
      if (ok > 0 && fail <= 0) {
        showToast?.(t('productAction.shelfDone', { label, count: ok, unit }), 'success');
      } else if (ok > 0 && fail > 0) {
        showToast?.(t('productAction.shelfPartial', { label, ok, fail, hint: failHint }), 'warning');
      } else {
        showToast?.(
          fail > 0
            ? t('productAction.shelfFailedCount', { label, fail, total: skcs || names.length, hint: failHint })
            : t('productAction.shelfNone', { label }),
          'error',
        );
      }
      setLoading(true);
      try {
        await loadProducts({ silent: true });
        await loadDimensionStats();
      } finally {
        setLoading(false);
      }
    } catch (e) {
      showToast?.(e?.message || t('productAction.shelfFailed', { label }), 'error');
    } finally {
      shelvingRef.current = false;
      setShelving(false);
    }
  }, [api, effectiveShopFilter, forcedShopId, isTiktokWorkspace, loadDimensionStats, loadProducts, showToast, t]);

  const resolveSelectedSpuNames = useCallback(() => {
    if (useOpsProductTable) {
      return Array.from(selectedSpuIds);
    }
    return items
      .filter((row) => selectedIds.has(String(row.id)))
      .map((row) => row.platformSpuId)
      .filter(Boolean);
  }, [useOpsProductTable, items, selectedIds, selectedSpuIds]);

  const onBatchShelfOn = useCallback(() => {
    return runModifySkcShelf(resolveSelectedSpuNames(), 1);
  }, [resolveSelectedSpuNames, runModifySkcShelf]);

  const onBatchShelfOff = useCallback(async () => {
    const names = resolveSelectedSpuNames()
      .map((s) => String(s || '').trim())
      .filter((s) => s && s !== '—');
    if (!names.length) {
      showToast?.(t('productAction.pickShelf', { label: isTiktokWorkspace ? t('productTable.deactivate') : t('productTable.listOff') }), 'warning');
      return;
    }
    const ok = await showPlatformConfirm({
      title: isTiktokWorkspace ? t('productAction.confirmDeactivate') : t('productAction.confirmUnlist'),
      message: isTiktokWorkspace
        ? t('productAction.confirmDeactivateMany', { count: names.length })
        : t('productAction.confirmUnlistMany', { count: names.length }),
      tone: 'warning',
      confirmText: isTiktokWorkspace ? t('productTable.deactivate') : t('productTable.listOff'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    return runModifySkcShelf(names, 2);
  }, [isTiktokWorkspace, resolveSelectedSpuNames, runModifySkcShelf, showPlatformConfirm, showToast, t]);

  const onShelfOne = useCallback(async (spuId, action) => {
    const spu = String(spuId || '').trim();
    if (!spu || spu === '—') {
      showToast?.(isTiktokWorkspace ? t('productAction.missingProductId') : t('productAction.missingSpu'), 'warning');
      return;
    }
    if (action === 'on') {
      return runModifySkcShelf([spu], 1);
    }
    const ok = await showPlatformConfirm({
      title: isTiktokWorkspace ? t('productAction.confirmDeactivate') : t('productAction.confirmUnlist'),
      message: isTiktokWorkspace
        ? t('productAction.confirmDeactivateOne', { id: spu })
        : t('productAction.confirmUnlistOne', { id: spu }),
      tone: 'warning',
      confirmText: isTiktokWorkspace ? t('productTable.deactivate') : t('productTable.listOff'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    return runModifySkcShelf([spu], 2);
  }, [isTiktokWorkspace, runModifySkcShelf, showPlatformConfirm, showToast, t]);

  const onSheinSyncStateChangeRef = useRef(onSheinSyncStateChange);
  onSheinSyncStateChangeRef.current = onSheinSyncStateChange;
  const onTemuSyncStateChangeRef = useRef(onTemuSyncStateChange);
  onTemuSyncStateChangeRef.current = onTemuSyncStateChange;
  const onTiktokSyncStateChangeRef = useRef(onTiktokSyncStateChange);
  onTiktokSyncStateChangeRef.current = onTiktokSyncStateChange;
  const actionRefs = useRef({});
  actionRefs.current = {
    refresh: onRefreshShopProducts,
    reloadList: async () => {
      setLoading(true);
      try {
        await loadProducts({ silent: true });
        await loadDimensionStats();
      } finally {
        setLoading(false);
      }
    },
    cancelSync: cancelProductSync,
    exportSelected: onExportSelected,
    exportPage: onExportPage,
    batchShelfOn: onBatchShelfOn,
    batchShelfOff: onBatchShelfOff,
    clearSelection: () => {
      setSelectedIds(new Set());
      setSelectedSpuIds(new Set());
    },
  };

  const publishSyncState = useCallback((notifyRef) => {
    if (!notifyRef.current) return;
    notifyRef.current({
      lastRefreshedAt: lastProductsSyncedAt,
      refreshing: refreshingProducts,
      listLoading: loading,
      listTotal: total,
      listPageCount: Array.isArray(items) ? items.length : 0,
      cooldownSeconds: refreshProductsCooldown,
      progress: syncProgress,
      cancellingSync,
      refresh: (...args) => actionRefs.current.refresh?.(...args),
      reloadList: (...args) => actionRefs.current.reloadList?.(...args),
      cancelSync: (...args) => actionRefs.current.cancelSync?.(...args),
      exportSelected: (...args) => actionRefs.current.exportSelected?.(...args),
      exportPage: (...args) => actionRefs.current.exportPage?.(...args),
      batchShelfOn: (...args) => actionRefs.current.batchShelfOn?.(...args),
      batchShelfOff: (...args) => actionRefs.current.batchShelfOff?.(...args),
      clearSelection: () => actionRefs.current.clearSelection?.(),
    });
  }, [
    lastProductsSyncedAt,
    refreshingProducts,
    loading,
    total,
    items,
    refreshProductsCooldown,
    syncProgress,
    cancellingSync,
  ]);

  useEffect(() => {
    if (!isSheinWorkspace) return;
    publishSyncState(onSheinSyncStateChangeRef);
  }, [isSheinWorkspace, publishSyncState]);

  useEffect(() => {
    if (!isTemuWorkspace) return;
    publishSyncState(onTemuSyncStateChangeRef);
  }, [isTemuWorkspace, publishSyncState]);

  useEffect(() => {
    if (!isTiktokWorkspace) return;
    publishSyncState(onTiktokSyncStateChangeRef);
  }, [isTiktokWorkspace, publishSyncState]);

  const onSyncPrice = async (productId) => {
    setLoading(true);
    try {
      await api.syncProductPrice(productId);
      showToast?.(t('productAction.priceRefreshed'), 'success');
      await loadProducts();
      await loadDimensionStats();
    } catch (e) {
      showToast?.(e.message || t('productAction.priceRefreshFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const allSelectableIds = useMemo(
    () => (useOpsProductTable
      ? (isTemuWorkspace
        ? collectTemuTreeProductIds(opsProductTree)
        : (isTiktokWorkspace
          ? collectTiktokTreeProductIds(opsProductTree)
          : collectSheinTreeProductIds(opsProductTree)))
      : items.map((row) => String(row.id))),
    [useOpsProductTable, isTemuWorkspace, isTiktokWorkspace, opsProductTree, items],
  );

  const allSelectableSpuIds = useMemo(
    () => (useOpsProductTable
      ? (isTemuWorkspace
        ? collectTemuTreeSpuKeys(opsProductTree)
        : (isTiktokWorkspace
          ? collectTiktokTreeSpuKeys(opsProductTree)
          : collectSheinTreeSpuKeys(opsProductTree)))
      : []),
    [useOpsProductTable, isTemuWorkspace, isTiktokWorkspace, opsProductTree],
  );

  const allPageSelected = useMemo(
    () => (useOpsProductTable
      ? allSelectableSpuIds.length > 0 && allSelectableSpuIds.every((id) => selectedSpuIds.has(id))
      : allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedIds.has(id))),
    [useOpsProductTable, allSelectableSpuIds, allSelectableIds, selectedSpuIds, selectedIds],
  );

  const somePageSelected = useMemo(
    () => (useOpsProductTable
      ? allSelectableSpuIds.some((id) => selectedSpuIds.has(id))
      : allSelectableIds.some((id) => selectedIds.has(id))),
    [useOpsProductTable, allSelectableSpuIds, allSelectableIds, selectedSpuIds, selectedIds],
  );

  const toggleSelectAll = () => {
    startTransition(() => {
      if (useOpsProductTable) {
        setSelectedSpuIds((prev) => togglePageSelection(prev, allSelectableSpuIds, allPageSelected));
        setSelectedIds((prev) => togglePageSelection(prev, allSelectableIds, allPageSelected));
        return;
      }
      setSelectedIds((prev) => togglePageSelection(prev, allSelectableIds, allPageSelected));
    });
  };

  const idsForSpuSelection = (spu) => {
    // TikTok：勾选维度是 product_id（树节点 spuId），不要用 SKU 雪花 ID
    if (isTiktokWorkspace) {
      const key = spuSelectionKey(spu);
      return key ? [key] : [];
    }
    return collectSpuProductIds(spu);
  };

  const toggleSpuSelected = (spu) => {
    const ids = idsForSpuSelection(spu);
    const spuKey = spuSelectionKey(spu);
    const currentlySelected = spuKey
      ? selectedSpuIds.has(spuKey)
      : (ids.length > 0 && ids.every((id) => selectedIds.has(id)));
    startTransition(() => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (currentlySelected) {
          ids.forEach((id) => next.delete(id));
        } else {
          ids.forEach((id) => next.add(id));
        }
        return next;
      });
      if (spuKey) {
        setSelectedSpuIds((prev) => {
          const next = new Set(prev);
          if (currentlySelected) next.delete(spuKey);
          else next.add(spuKey);
          return next;
        });
      }
    });
  };

  const isSpuSelected = (spu) => {
    const spuKey = spuSelectionKey(spu);
    if (spuKey && selectedSpuIds.has(spuKey)) return true;
    const ids = idsForSpuSelection(spu);
    return ids.length > 0 && ids.every((id) => selectedIds.has(id));
  };

  const openProductEdit = useCallback(async (spuId, editPath) => {
    const shopId = String(forcedShopIdRef.current || effectiveShopFilter || '').trim();
    const spu = String(spuId || '').trim();
    if (!shopId) {
      showToast?.(t('productAction.pickShop'), 'warning');
      return;
    }
    if (!spu || spu === '—') {
      showToast?.(isTiktokWorkspace ? t('productAction.missingProductIdEdit') : t('productAction.missingSpuEdit'), 'warning');
      return;
    }
    setEditingSpuId(spu);
    try {
      const check = isTemuWorkspace
        ? await checkTemuProductEditable(shopId, spu)
        : (isTiktokWorkspace
          ? await checkTiktokProductEditable(shopId, spu)
          : await checkSheinProductEditable(shopId, spu));
      if (!check.ok) {
        showToast?.(check.reason || t('productAction.notEditable'), 'warning');
        return;
      }
      if (isTemuWorkspace) openTemuPathInNewTab(editPath);
      else if (isTiktokWorkspace) openTiktokPathInNewTab(editPath);
      else openSheinPathInNewTab(editPath);
    } finally {
      setEditingSpuId('');
    }
  }, [effectiveShopFilter, isTemuWorkspace, isTiktokWorkspace, showToast, t]);

  const copySpuToDraft = useCallback(async (spuId, spuTitle = '') => {
    const shopId = String(forcedShopIdRef.current || effectiveShopFilter || '').trim();
    const spu = String(spuId || '').trim();
    if (!shopId) {
      showToast?.(t('productAction.pickShop'), 'warning');
      return;
    }
    if (!spu || spu === '—') {
      showToast?.(isTiktokWorkspace ? t('productAction.missingProductIdCopy') : t('productAction.missingSpuCopy'), 'warning');
      return;
    }
    if (typeof api.copyProductToPublishDraft !== 'function') {
      showToast?.(t('productAction.copyUnsupported'), 'warning');
      return;
    }
    if (copyingSpuId) return;
    // TEMU：先选目标站点，再写入草稿 selectedSites
    if (isTemuWorkspace) {
      setCopySiteModal({ spuId: spu, title: String(spuTitle || '').trim() });
      return;
    }
    setCopyingSpuId(spu);
    try {
      const res = await api.copyProductToPublishDraft(Number(shopId), { spuName: spu });
      const draftId = res?.id;
      showToast?.(t('productAction.copied'), 'success');
      const publishPath = draftId
        ? `products/publish?draftId=${encodeURIComponent(draftId)}`
        : 'products/publish';
      if (isTiktokWorkspace) openTiktokPathInNewTab(publishPath);
      else openSheinPathInNewTab(publishPath);
    } catch (e) {
      showToast?.(e?.message || t('productAction.copyFailed'), 'error');
    } finally {
      setCopyingSpuId('');
    }
  }, [api, copyingSpuId, effectiveShopFilter, isTemuWorkspace, isTiktokWorkspace, showToast, t]);

  const confirmTemuCopyWithSites = useCallback(async (selectedSites) => {
    const shopId = String(forcedShopIdRef.current || effectiveShopFilter || '').trim();
    const spu = String(copySiteModal?.spuId || '').trim();
    if (!shopId || !spu) {
      showToast?.(t('productAction.missingShopSpu'), 'warning');
      return;
    }
    if (copyingSpuId) return;
    setCopyingSpuId(spu);
    try {
      const res = await temuPlatformAPI.copyProductToPublishDraft(Number(shopId), {
        spuName: spu,
        selectedSites,
      });
      const draftId = res?.id;
      showToast?.(t('productAction.copied'), 'success');
      setCopySiteModal(null);
      const publishPath = draftId
        ? `products/publish?draftId=${encodeURIComponent(draftId)}`
        : 'products/publish';
      openTemuPathInNewTab(publishPath);
    } catch (e) {
      showToast?.(e?.message || t('productAction.copyFailed'), 'error');
    } finally {
      setCopyingSpuId('');
    }
  }, [copySiteModal, copyingSpuId, effectiveShopFilter, showToast, t]);

  const temuCopySiteOptions = useMemo(() => {
    const fromShop = [];
    siteNameByAbbr.forEach((name, abbr) => {
      const value = String(abbr || '').trim();
      if (!value) return;
      fromShop.push({
        value,
        label: name && name !== value
          ? t('productCopy.siteCode', { name, code: value })
          : (nameOfSiteId(value) || value),
      });
    });
    if (fromShop.length) return fromShop;
    const fallbackIds = ['100', '101', '102', '103', '104', '105', '106', '107', '108', '109', '110', '118'];
    return fallbackIds.map((id) => ({
      value: id,
      label: t(`temuSite.s${id}`, { defaultValue: nameOfSiteId(id) || id }),
    }));
  }, [siteNameByAbbr, t]);

  const pullLabel = isSheinWorkspace
    ? t('productTable.pullShein')
    : isTemuWorkspace
      ? t('productTable.pullTemu')
      : t('productTable.pullTiktok');
  const emptyHintKey = listingDimension === 'drafts' && isTiktokWorkspace
    ? 'emptyTiktokDrafts'
    : ({
      all: 'emptyAll',
      listed: 'emptyListed',
      pending: 'emptyPending',
      sold_out: 'emptySoldOut',
      offline: 'emptyOffline',
      drafts: 'emptyDrafts',
      price_review: 'emptyPriceReview',
    })[listingDimension] || 'emptyListed';
  const emptyDescription = (isSheinWorkspace || isTemuWorkspace || isTiktokWorkspace)
    ? t(`productTable.${emptyHintKey}`, { pull: pullLabel })
    : t('productTable.emptyGeneric');

  // TEMU 价格列已整列隐藏（见上方 showPriceColumn），列跨度随之少 1。
  const tableColSpan = isTemuWorkspace ? 9 : 11;
  const pendingPriceReviewCount = Number(dimensionStats?.priceReview) || 0;

  const renderOpsTableBody = () => {
    if (opsProductTree.length === 0) {
      return (
        <tr>
          <td colSpan={tableColSpan} className="wf-tasks-table__empty">
            <div className="funding-empty-state">
              <p className="funding-empty-state__title">{t('productTable.emptyTitle')}</p>
              <p className="funding-empty-state__description">{emptyDescription}</p>
            </div>
          </td>
        </tr>
      );
    }

    const rows = [];
    opsProductTree.forEach((spu) => {
      const skcList = spu.skcs.length
        ? spu.skcs
        : [{ key: `${spu.key}-empty`, skcId: '—', image: null, specLabel: '', skus: [] }];
      const spuRowSpan = Math.max(1, skcList.length);
      let spuRowStarted = false;
      const buildChildPath = isTemuWorkspace
        ? buildTemuProductChildPath
        : (isTiktokWorkspace ? buildTiktokProductChildPath : buildSheinProductChildPath);
      const editPath = buildChildPath(
        spu.spuId && spu.spuId !== '—' ? `edit/${encodeURIComponent(spu.spuId)}` : 'edit',
      );

      skcList.forEach((skc, skcIdx) => {
        const isFirstSpuRow = !spuRowStarted;
        if (isFirstSpuRow) spuRowStarted = true;
        const representative = pickSkcRepresentative(skc.skus);
        // 列表接口每个 SKC 只回 1 条预览 SKU 行：真实 SKU 数与库存合计读后端随行带回的
        // skuCount / stockTotal，缺字段（SHEIN / TikTok / 老接口）时退回前端按明细行汇总。
        const skuCountTotal = Number(skc.skuCount) > 0 ? Number(skc.skuCount) : (skc.skus || []).length;
        const stockTotal = skc.stockTotal != null
          ? skc.stockTotal
          : sumSkcStock(skc.skus, 'stockAvailableQty');
        const previewSkus = (skc.skus || []).slice(0, SKU_PREVIEW_LIMIT);
        const hasMoreSkus = skuCountTotal > previewSkus.length;
        const rowKey = `${spu.key}-${skc.key}-${skcIdx}`;

        rows.push(
          <tr key={rowKey} className="shein-product-row">
            {isFirstSpuRow ? (
              <>
                <td
                  rowSpan={spuRowSpan}
                  className={
                    isTemuWorkspace
                      ? 'shein-product-cell temu-product-cell--spu-check'
                      : 'shein-product-cell shein-product-cell--spu-check'
                  }
                  onClick={() => toggleSpuSelected(spu)}
                >
                  <input
                    type="checkbox"
                    aria-label={isTiktokWorkspace ? t('productTable.selectProduct', { id: spu.spuId }) : t('productTable.selectSpu', { id: spu.spuId })}
                    checked={isSpuSelected(spu)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSpuSelected(spu)}
                  />
                </td>
                <td rowSpan={spuRowSpan} className="shein-product-cell shein-product-cell--info">
                  <div className="shein-product-info shein-product-info--text-only">
                    <div className="shein-product-info__text">
                      <HoverCopyField
                        value={spu.title}
                        className="shein-product-info__title"
                        popoverCopy
                      />
                      <HoverCopyField
                        label={isTiktokWorkspace ? t('productTable.productId') : t('productTable.spu')}
                        value={spu.spuId && spu.spuId !== '—' ? spu.spuId : ''}
                        className="shein-product-info__meta"
                      />
                      {!isTemuWorkspace && spu.supplierCode ? (
                        <HoverCopyField
                          label={t('productTable.supplier')}
                          value={spu.supplierCode}
                          className="shein-product-info__meta"
                        />
                      ) : null}
                    </div>
                  </div>
                </td>
              </>
            ) : null}

            <td className="shein-product-cell shein-product-cell--spec">
              <div className="shein-product-spec">
                <ProductThumb imageUrl={skc.image} alt={skc.specLabel || (isTiktokWorkspace ? t('productTable.mainSpec') : 'SKC')} />
                <div className="shein-product-spec__text">
                  {skc.specLabel ? <div className="shein-product-spec__color">{skc.specLabel}</div> : null}
                  {!isTiktokWorkspace ? (
                    <HoverCopyField
                      label="SKC"
                      value={skc.skcId && skc.skcId !== '—' ? skc.skcId : ''}
                      className="shein-product-spec__skc"
                    />
                  ) : null}
                  {isTemuWorkspace ? (
                    <HoverCopyField
                      label={t('productTable.supplier')}
                      value={skc.supplierCode || ''}
                      className="shein-product-spec__skc"
                      emptyText="—"
                    />
                  ) : null}
                </div>
              </div>
            </td>

            <td className="shein-product-cell shein-product-cell--merchant-sku">
              <div className="shein-product-sku-preview">
                {previewSkus.length ? (
                  <>
                    {previewSkus.map((sku, idx) => (
                      <div key={sku?.id || `${skc.key}-merchant-${idx}`} className="shein-product-sku-preview__item">
                        {isTiktokWorkspace ? (
                          <div className="shein-product-sku-preview__size">{t('productTable.size', { size: skuSizeLabel(sku) })}</div>
                        ) : null}
                        <HoverCopyField
                          label={isTiktokWorkspace ? t('productTable.merchantSku') : undefined}
                          value={skuMerchantCode(sku)}
                          className="shein-product-sku-preview__code"
                          emptyText="—"
                        />
                      </div>
                    ))}
                    {hasMoreSkus ? (
                      <button
                        type="button"
                        className="shein-product-sku-preview__more"
                        onClick={() => setSkuModalSkc(skc)}
                      >
                        {t('productTable.viewMore', { count: skuCountTotal })}
                      </button>
                    ) : null}
                  </>
                ) : (
                  <div className="shein-product-sku-preview__item shein-product-sku-preview__item--empty">—</div>
                )}
              </div>
            </td>

            {!isTemuWorkspace ? (
            <td className="shein-product-cell shein-product-cell--sku">
              <div className="shein-product-sku-preview">
                {previewSkus.length ? (
                  <>
                    {previewSkus.map((sku, idx) => (
                      <div key={sku?.id || `${skc.key}-sku-${idx}`} className="shein-product-sku-preview__item">
                        <div className="shein-product-sku-preview__size">{t('productTable.size', { size: skuSizeLabel(sku) })}</div>
                        <HoverCopyField
                          label="SKU"
                          value={sku?.platformSkuId || ''}
                          className="shein-product-sku-preview__code"
                          emptyText="—"
                        />
                      </div>
                    ))}
                    {hasMoreSkus ? (
                      <button
                        type="button"
                        className="shein-product-sku-preview__more"
                        onClick={() => setSkuModalSkc(skc)}
                      >
                        {t('productTable.viewMore', { count: skuCountTotal })}
                      </button>
                    ) : null}
                  </>
                ) : (
                  <div className="shein-product-sku-preview__item shein-product-sku-preview__item--empty">—</div>
                )}
              </div>
            </td>
            ) : null}

            {showPriceColumn ? (
            <td className="shein-product-cell shein-product-cell--price">
              <div className="shein-product-price">
                {formatSkcPriceRange(
                  skc.skus,
                  representative?.currency || 'USD',
                )}
              </div>
              {Array.isArray(representative?.priceSiteNames) && representative.priceSiteNames.length > 1 ? (
                <div
                  className="shein-product-price__hint"
                  title={representative.priceSiteNames.join('\n')}
                >
                  {t('productTable.sitePrices', { count: representative.priceSiteNames.length })}
                </div>
              ) : null}
              {representative?.priceReviewStatus === 'pending' ? (
                <div className="shein-product-price__hint">{t('productTable.pendingReview')}</div>
              ) : null}
            </td>
            ) : null}

            <td className="shein-product-cell shein-product-cell--stock">
              {formatStock(stockTotal)}
            </td>

            <td className="shein-product-cell shein-product-cell--status">
              {representative ? (
                (() => {
                  const statusRow = {
                    ...representative,
                    stockQty: stockTotal != null ? stockTotal : representative.stockQty,
                    listedAt: (skc.skus || []).find((s) => s?.listedAt)?.listedAt || representative.listedAt,
                  };
                  const statusCode = shelfStatusCode(statusRow);
                  const statusLabel = statusCode ? t(`productTable.${statusCode}`) : '—';
                  return (
                    <div className="shein-product-status-block">
                      <span className={`shein-product-shelf shein-product-shelf--${shelfStatusClass(statusRow)}`}>
                        {statusLabel}
                      </span>
                    </div>
                  );
                })()
              ) : '—'}
            </td>

            <td className="shein-product-cell shein-product-cell--sites">
              {representative || (isTiktokWorkspace && tiktokShopSiteNames.length) ? (
                (() => {
                  let siteEntries = representative
                    ? buildSiteEntries(
                      representative,
                      siteNameByAbbr,
                      resolveSiteDisplayName,
                    )
                    : [];
                  // TikTok 商品无 listedSites：用当前店铺站点
                  if (isTiktokWorkspace && !siteEntries.length && tiktokShopSiteNames.length) {
                    siteEntries = tiktokShopSiteNames.map((name) => ({
                      name,
                      price: null,
                      currency: representative?.currency || null,
                    }));
                  }
                  if (!siteEntries.length) return '—';
                  const previewSites = siteEntries.slice(0, SITE_PREVIEW_LIMIT);
                  const hasMoreSites = siteEntries.length > SITE_PREVIEW_LIMIT;
                  return (
                    <div className="shein-product-sites" title={siteEntries.map((s) => s.name).join('\n')}>
                      {previewSites.map((site) => (
                        <div key={site.name} className="shein-product-sites__item">{site.name}</div>
                      ))}
                      {hasMoreSites ? (
                        <button
                          type="button"
                          className="shein-product-sites__more"
                          onClick={() => setSitesModal({
                            title: spu.title,
                            skcId: isTiktokWorkspace ? '' : skc.skcId,
                            specLabel: skc.specLabel,
                            sites: siteEntries,
                          })}
                        >
                          {t('productTable.viewMore', { count: siteEntries.length })}
                        </button>
                      ) : null}
                    </div>
                  );
                })()
              ) : '—'}
            </td>

            <td className="shein-product-cell shein-product-cell--time">
              {representative ? (
                (() => {
                  const timeRow = {
                    ...representative,
                    listedAt: (skc.skus || []).find((s) => s?.listedAt)?.listedAt || representative.listedAt,
                  };
                  return (
                    <>
                      <div>{t('productTable.createdAt', { time: formatDateTime(createTimeOf(timeRow, isTemuWorkspace)) })}</div>
                      <div>{t('productTable.publishedAt', { time: formatDateTime(publishTimeOf(timeRow)) })}</div>
                      <div>{t('productTable.listedAt', { time: formatDateTime(listedTimeOf(timeRow)) })}</div>
                    </>
                  );
                })()
              ) : '—'}
            </td>

            {isFirstSpuRow ? (
              <td rowSpan={spuRowSpan} className="shein-product-cell shein-product-cell--actions">
                <div className="shein-product-actions">
                  <Link
                    to={editPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`wf-tasks-actions__link shein-product-action--primary${editingSpuId === spu.spuId ? ' is-busy' : ''}`}
                    aria-disabled={editingSpuId === spu.spuId || !spu.spuId || spu.spuId === '—'}
                    onClick={(e) => {
                      e.preventDefault();
                      if (editingSpuId) return;
                      openProductEdit(spu.spuId, editPath);
                    }}
                  >
                    {editingSpuId === spu.spuId ? t('productTable.checking') : t('productTable.edit')}
                  </Link>
                  <button
                    type="button"
                    className="wf-tasks-actions__link"
                    disabled={loading || copyingSpuId === spu.spuId || !spu.spuId || spu.spuId === '—'}
                    onClick={() => copySpuToDraft(spu.spuId, spu.title)}
                  >
                    {copyingSpuId === spu.spuId ? t('productTable.copying') : t('productTable.copy')}
                  </button>
                  <SheinProductMoreMenu
                    disabled={loading || shelving || !spu.spuId || spu.spuId === '—'}
                    onShelfOn={() => onShelfOne(spu.spuId, 'on')}
                    onShelfOff={() => onShelfOne(spu.spuId, 'off')}
                    shelfOnLabel={isTiktokWorkspace ? t('productTable.activate') : t('productTable.listOn')}
                    shelfOffLabel={isTiktokWorkspace ? t('productTable.deactivate') : t('productTable.listOff')}
                  />
                </div>
              </td>
            ) : null}
          </tr>,
        );
      });
    });

    return rows;
  };

  return (
    <>
    <section className={`wf-tasks-page platform-product-page${embedded ? ' wf-tasks-page--embedded' : ''}${loading ? ' platform-product-page--loading' : ''}${opsExternalChrome ? ' platform-product-page--ops-chrome' : ''}${isTemuWorkspace ? ' platform-product-page--temu' : ''}${isTiktokWorkspace ? ' platform-product-page--tiktok' : ''}`}>
      <div className="wf-tasks-page__shell">
        {!opsExternalChrome ? (
        <>
        <header className={`wf-tasks-toolbar${isSheinWorkspace && enableSheinOpsToolbar ? ' wf-tasks-toolbar--shein-ops' : ''}`} aria-label={isSheinWorkspace ? t('productFilter.filterShein') : (isTemuWorkspace ? t('productFilter.filterTemu') : (isTiktokWorkspace ? t('productFilter.filterTiktok') : t('productFilter.filterGeneric')))}>
          <div className="wf-tasks-toolbar__primary">
            {isSheinWorkspace && !sheinExternalToolbar ? (
              <SheinRefreshButton
                onClick={onRefreshShopProducts}
                loading={refreshingProducts}
                cooldownSeconds={refreshProductsCooldown}
                busyLabel={t('productFilter.refreshing')}
                className="wf-tasks-btn wf-tasks-btn--query"
              >
                {t('productFilter.refresh')}
              </SheinRefreshButton>
            ) : null}
            {!isSheinWorkspace ? (
              <button type="button" className="wf-tasks-btn wf-tasks-btn--query" disabled={loading} onClick={onSyncShop}>
                {(isTemuWorkspace || isTiktokWorkspace)
                  ? (isTemuWorkspace ? t('productTable.pullTemu') : t('productTable.pullTiktok'))
                  : t('productFilter.syncShop')}
              </button>
            ) : null}
            {isSheinWorkspace && enableSheinOpsToolbar ? (
              <div className="shein-product-export">
                <button
                  type="button"
                  className="wf-tasks-btn wf-tasks-btn--ghost"
                  disabled={loading || batchBusy}
                  aria-expanded={exportMenuOpen}
                  onClick={() => setExportMenuOpen((v) => !v)}
                >
                  {t('productFilter.exportProducts')}
                </button>
                {exportMenuOpen ? (
                  <div className="shein-product-export__menu" role="menu">
                    <button type="button" role="menuitem" onClick={onExportSelected}>
                      {t('productFilter.exportSelected')}
                    </button>
                    <button type="button" role="menuitem" onClick={onExportPage}>
                      {t('productFilter.exportThisPage')}
                    </button>
                    <button type="button" role="menuitem" onClick={onExportFilteredHint}>
                      {t('productFilter.exportByFilter')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="wf-tasks-toolbar__filters">
            {isSheinWorkspace && enableSheinOpsToolbar ? (
              <>
                <div className="wf-tasks-toolbar__field">
                  <input
                    type="text"
                    className="wf-tasks-filter wf-tasks-toolbar__control"
                    placeholder="SKC"
                    value={filterDraft.skc}
                    onChange={(e) => updateFilterDraft('skc', e.target.value)}
                    aria-label={t('productFilter.filterSkc')}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                </div>
                <div className="wf-tasks-toolbar__field">
                  <input
                    type="text"
                    className="wf-tasks-filter wf-tasks-toolbar__control"
                    placeholder={t('productTable.supplier')}
                    value={filterDraft.supplierCode}
                    onChange={(e) => updateFilterDraft('supplierCode', e.target.value)}
                    aria-label={t('productFilter.filterSupplier')}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                </div>
                <div className="wf-tasks-toolbar__field wf-tasks-toolbar__field--wide">
                  <input
                    type="text"
                    className="wf-tasks-filter wf-tasks-toolbar__control"
                    placeholder={t('productFilter.productName')}
                    value={filterDraft.title}
                    onChange={(e) => updateFilterDraft('title', e.target.value)}
                    aria-label={t('productFilter.filterProductName')}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                </div>
                <div className="wf-tasks-toolbar__field">
                  <input
                    type="text"
                    className="wf-tasks-filter wf-tasks-toolbar__control"
                    placeholder={t('productFilter.brand')}
                    value={filterDraft.brand}
                    onChange={(e) => updateFilterDraft('brand', e.target.value)}
                    aria-label={t('productFilter.filterBrand')}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                </div>
                <div className="wf-tasks-toolbar__field">
                  <input
                    type="text"
                    className="wf-tasks-filter wf-tasks-toolbar__control"
                    placeholder={t('productTable.sku')}
                    value={filterDraft.sku}
                    onChange={(e) => updateFilterDraft('sku', e.target.value)}
                    aria-label={t('productFilter.filterSku')}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                </div>
              </>
            ) : (
              <div className="wf-tasks-toolbar__field wf-tasks-toolbar__field--clearable">
                <input
                  type="text"
                  className="wf-tasks-filter wf-tasks-toolbar__control"
                  placeholder={isTiktokWorkspace ? t('productFilter.searchTiktok') : t('productFilter.searchDefault')}
                  value={keywordDraft}
                  onChange={(e) => setKeywordDraft(e.target.value)}
                  aria-label={t('productFilter.searchAria')}
                  onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                />
              </div>
            )}
            {!isSheinWorkspace ? (
              <div className="wf-tasks-toolbar__field">
                <IpeToolbarSelect
                  value={shopFilter}
                  onChange={setShopFilter}
                  options={shopOptions}
                  ariaLabel={t('productFilter.filterShop')}
                />
              </div>
            ) : null}
            {!isSheinWorkspace ? (
              <>
                {/* TEMU 不展示「核价状态」筛选：核价/调价接口同属自研专属接口（ISV 必然 7000019/7000022），
                    后端 mapper 只能把 priceReviewStatus 落成 unknown，筛选项恒等于无效。 */}
                {showPriceColumn ? (
                  <div className="wf-tasks-toolbar__field">
                    <IpeToolbarSelect
                      value={priceReviewFilter}
                      onChange={setPriceReviewFilter}
                      options={priceReviewOptions}
                      ariaLabel={t('productFilter.filterReview')}
                    />
                  </div>
                ) : null}
                <div className="wf-tasks-toolbar__field">
                  <IpeToolbarSelect
                    value={publishFilter}
                    onChange={setPublishFilter}
                    options={publishStatusOptions}
                    ariaLabel={t('productFilter.filterPublish')}
                  />
                </div>
              </>
            ) : null}
            <button type="button" className="wf-tasks-btn wf-tasks-btn--query" disabled={loading} onClick={onQuery}>
              {t('productFilter.query')}
            </button>
            <button type="button" className="wf-tasks-btn wf-tasks-btn--ghost" disabled={loading} onClick={onReset}>
              {t('productFilter.reset')}
            </button>
          </div>
        </header>

        {isSheinWorkspace ? (
          <>
            {pendingPriceReviewCount > 0 && listingDimension !== 'price_review' && !hideDimensionTabs ? (
              <div className="shein-product-price-alert" role="status">
                <span className="shein-product-price-alert__text">
                  <Trans
                    i18nKey="productFilter.priceAlert"
                    ns="workbench"
                    values={{ count: pendingPriceReviewCount }}
                    components={{ strong: <strong /> }}
                  />
                </span>
                <button
                  type="button"
                  className="shein-product-price-alert__action"
                  onClick={() => {
                    if (lockListingDimension) return;
                    startTransition(() => {
                      setListingDimension('price_review');
                      setPage(1);
                    });
                  }}
                >
                  {t('productFilter.goReview')}
                </button>
              </div>
            ) : null}
            {!hideDimensionTabs ? (
              <div className="shein-product-dimensions" role="tablist" aria-label={t('productFilter.dimensionAria')}>
                {SHEIN_LISTING_DIMENSIONS.map((dim) => {
                  const count = dim.id === 'all'
                    ? (Number(dimensionStats?.total) || 0)
                    : (Number(dimensionStats?.[dim.statKey]) || 0);
                  const isActive = listingDimension === dim.id;
                  return (
                    <button
                      key={dim.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      disabled={lockListingDimension && dim.id !== listingDimension}
                      className={[
                        'shein-product-dimensions__tab',
                        isActive ? 'shein-product-dimensions__tab--active' : '',
                        dim.accent && count > 0 ? 'shein-product-dimensions__tab--accent' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => {
                        if (lockListingDimension) return;
                        startTransition(() => {
                          setListingDimension(dim.id);
                          setPage(1);
                        });
                      }}
                    >
                      {t(dim.labelKey)}
                      <span className="shein-product-dimensions__count">{count}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </>
        ) : null}
        </>
        ) : null}

        <div className="wf-tasks-table-panel">
          <div className="wf-tasks-table-wrap" role="region" aria-live="polite">
            <table className={`wf-tasks-table platform-product-table${useOpsProductTable ? ' platform-product-table--shein' : ''}${isTemuWorkspace ? ' platform-product-table--temu' : ''}${isTiktokWorkspace ? ' platform-product-table--tiktok' : ''}`}>
              {useOpsProductTable ? (
                <>
                  <colgroup>
                    <col className={isTemuWorkspace ? 'temu-product-col--check' : (isTiktokWorkspace ? 'tiktok-product-col--check' : 'shein-product-col--check')} />
                    <col className={isTemuWorkspace ? 'temu-product-col--info' : (isTiktokWorkspace ? 'tiktok-product-col--info' : 'shein-product-col--info')} />
                    <col className={isTemuWorkspace ? 'temu-product-col--skc' : (isTiktokWorkspace ? 'tiktok-product-col--skc' : 'shein-product-col--skc')} />
                    <col className={isTemuWorkspace ? 'temu-product-col--merchant' : (isTiktokWorkspace ? 'tiktok-product-col--merchant' : 'shein-product-col--merchant')} />
                    {!isTemuWorkspace ? <col className={isTiktokWorkspace ? 'tiktok-product-col--sku' : 'shein-product-col--sku'} /> : null}
                    {showPriceColumn ? (
                      <col className={isTemuWorkspace ? 'temu-product-col--price' : (isTiktokWorkspace ? 'tiktok-product-col--price' : 'shein-product-col--price')} />
                    ) : null}
                    <col className={isTemuWorkspace ? 'temu-product-col--stock' : (isTiktokWorkspace ? 'tiktok-product-col--stock' : 'shein-product-col--stock')} />
                    <col className={isTemuWorkspace ? 'temu-product-col--status' : (isTiktokWorkspace ? 'tiktok-product-col--status' : 'shein-product-col--status')} />
                    <col className={isTemuWorkspace ? 'temu-product-col--sites' : (isTiktokWorkspace ? 'tiktok-product-col--sites' : 'shein-product-col--sites')} />
                    <col className={isTemuWorkspace ? 'temu-product-col--time' : (isTiktokWorkspace ? 'tiktok-product-col--time' : 'shein-product-col--time')} />
                    <col className={isTemuWorkspace ? 'temu-product-col--ops' : (isTiktokWorkspace ? 'tiktok-product-col--ops' : 'shein-product-col--ops')} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className={isTemuWorkspace ? 'temu-product-cell--spu-check' : 'shein-product-cell--spu-check'}
                        onClick={() => {
                          if (allSelectableIds.length === 0) return;
                          toggleSelectAll();
                        }}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('productTable.selectPage')}
                          checked={allPageSelected}
                          ref={(el) => {
                            if (!el) return;
                            el.indeterminate = somePageSelected && !allPageSelected;
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th scope="col">{t('productTable.productInfo')}</th>
                      <th scope="col">{isTiktokWorkspace ? t('productTable.mainSpec') : t('productTable.mainSpecSkc')}</th>
                      <th scope="col">{t('productTable.merchantSku')}</th>
                      {!isTemuWorkspace ? <th scope="col">{t('productTable.sku')}</th> : null}
                      {showPriceColumn ? <th scope="col">{t('productTable.price')}</th> : null}
                      <th scope="col">{t('productTable.stock')}</th>
                      <th scope="col">{t('productTable.status')}</th>
                      <th scope="col">{t('productTable.site')}</th>
                      <th scope="col">{t('productTable.time')}</th>
                      <th scope="col" className="shein-product-th--actions">{t('productTable.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>{renderOpsTableBody()}</tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th scope="col">{t('productTable.thumb')}</th>
                      <th scope="col">{t('productTable.title')}</th>
                      <th scope="col">{t('productTable.shop')}</th>
                      <th scope="col">{t('productTable.spuSkc')}</th>
                      <th scope="col">{t('productTable.priceReviewCol')}</th>
                      <th scope="col">{t('productTable.supplyPrice')}</th>
                      <th scope="col">{t('productTable.salePrice')}</th>
                      <th scope="col">{t('productTable.publishStatus')}</th>
                      <th scope="col">{t('productTable.syncedAt')}</th>
                      <th scope="col">{t('productTable.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={tableColSpan} className="wf-tasks-table__empty">
                          <div className="funding-empty-state">
                            <p className="funding-empty-state__title">{t('productTable.emptyTitle')}</p>
                            <p className="funding-empty-state__description">{emptyDescription}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      items.map((row) => (
                        <tr key={row.id}>
                          <td className="platform-product-table__thumb">
                            {row.primaryImageUrl ? (
                              <SignedImage src={row.primaryImageUrl} alt="" className="platform-product-table__img" />
                            ) : (
                              <span className="platform-product-table__img-placeholder">—</span>
                            )}
                          </td>
                          <td className="platform-product-table__title">{row.title || '—'}</td>
                          <td>{row.shopName || '—'}</td>
                          <td className="platform-product-table__ids">
                            <div>{row.platformSpuId || '—'}</div>
                            <div className="platform-product-table__sub">{row.platformSkcId || '—'}</div>
                          </td>
                          <td>
                            <span className={`wf-tasks-status wf-tasks-status--${priceReviewClass(row.priceReviewStatus)}`}>
                              {(() => {
                                const reviewKey = {
                                  pending: 'pendingReview',
                                  approved: 'reviewApproved',
                                  rejected: 'reviewRejected',
                                  unknown: 'reviewUnknown',
                                }[row.priceReviewStatus];
                                return reviewKey ? t(`productTable.${reviewKey}`) : (row.priceReviewStatus || '—');
                              })()}
                            </span>
                            {row.priceReviewMessage ? (
                              <div className="platform-product-table__hint" title={row.priceReviewMessage}>
                                {row.priceReviewMessage}
                              </div>
                            ) : null}
                          </td>
                          <td>{row.supplyPrice ?? '—'}</td>
                          <td>{row.salePrice ?? '—'}</td>
                          <td>{(() => {
                            const publishKey = {
                              published: 'statusPublished',
                              pending: 'pending',
                              draft: 'draft',
                              failed: 'publishFailedAudit',
                              offline: 'offline',
                            }[row.publishStatus];
                            return publishKey ? t(`productTable.${publishKey}`) : (row.publishStatus || '—');
                          })()}</td>
                          <td className="platform-product-table__muted">{formatDateTime(row.lastSyncedAt || row.updatedAt)}</td>
                          <td>
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={loading}
                              onClick={() => onSyncPrice(row.id)}
                            >
                              {t('productTable.refreshPrice')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </>
              )}
            </table>
          </div>
        </div>

        {opsExternalChrome || useOpsProductTable ? (
          <SheinOpsPager
            page={page}
            pageSize={pageSize}
            total={total}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={t('productTable.pager')}
          />
        ) : (
          <WfTasksPagination
            variant="element"
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={t('productTable.pager')}
          />
        )}
      </div>
    </section>
    <SkuDetailModal
      open={Boolean(skuModalSkc)}
      skc={skuModalSkc}
      loadSkus={isTemuWorkspace ? loadTemuSkcSkus : null}
      showPrice={showPriceColumn}
      onClose={() => setSkuModalSkc(null)}
    />
    <SitesDetailModal
      open={Boolean(sitesModal)}
      title={sitesModal?.title}
      skcId={sitesModal?.skcId}
      specLabel={sitesModal?.specLabel}
      sites={sitesModal?.sites || []}
      onClose={() => setSitesModal(null)}
    />
    <TemuCopyProductSitesModal
      open={Boolean(copySiteModal)}
      title={copySiteModal?.title || ''}
      sites={temuCopySiteOptions}
      busy={Boolean(copyingSpuId)}
      onClose={() => {
        if (copyingSpuId) return;
        setCopySiteModal(null);
      }}
      onConfirm={confirmTemuCopyWithSites}
    />
    </>
  );
}
